// src/pages/Settings/SupportForm.js
import React, { useState } from 'react';
import { FiHelpCircle, FiSend, FiUpload, FiCheck, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { submitSupportRequest } from '../../services/supportService';
import './SupportForm.css';

const SupportForm = () => {
  const { t } = useTranslation();
  
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 30 * 1024 * 1024; // 30MB
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image file (JPEG, PNG, JPG, or GIF)');
      return;
    }
    
    if (file.size > maxSize) {
      alert('File size must be less than 30MB');
      return;
    }
    
    setImage(file);
    
    // Create image preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Remove selected image
  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
  };
  
  // Submit the support request
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email.trim() || !message.trim()) {
      alert(t('errors.requiredFields'));
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert(t('errors.invalidEmail'));
      return;
    }
    
    setIsSubmitting(true);
    setSubmitResult(null);
    
    try {
      const result = await submitSupportRequest({
        email,
        message,
        category,
        image
      });
      
      setSubmitResult(result);
      
      if (result.success) {
        // Reset form on success
        setEmail('');
        setMessage('');
        setCategory('general');
        setImage(null);
        setImagePreview(null);
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setSubmitResult({
        success: false,
        error: t('settings.support.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Support categories
  const categories = [
    { value: 'general', label: t('settings.support.categories.general') },
    { value: 'bug', label: t('settings.support.categories.bug') },
    { value: 'feature', label: t('settings.support.categories.feature') },
    { value: 'account', label: t('settings.support.categories.account') },
    { value: 'other', label: t('settings.support.categories.other') }
  ];
  
  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <h3><FiHelpCircle /> {t('settings.support.title')}</h3>
      </div>
      <div className="settings-card-content">
        <p className="settings-description">
          {t('settings.support.description')}
        </p>
        
        {submitResult && (
          <div className={`support-result ${submitResult.success ? 'success' : 'error'}`}>
            {submitResult.success ? (
              <><FiCheck className="icon" /> {t('settings.support.success')}</>
            ) : (
              <><FiX className="icon" /> {submitResult.error || t('settings.support.error')}</>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="support-form">
          <div className="settings-field">
            <label htmlFor="support-email">{t('settings.support.email')} *</label>
            <input
              id="support-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="settings-input"
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div className="settings-field">
            <label htmlFor="support-category">{t('settings.support.category')}</label>
            <select
              id="support-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="settings-select"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="settings-field">
            <label htmlFor="support-message">{t('settings.support.message')} *</label>
            <textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="settings-textarea"
              placeholder={t('settings.support.message')}
              rows="4"
              required
            ></textarea>
          </div>
          
          <div className="settings-field">
            <label>
              {t('settings.support.screenshot')}
              <span className="optional-tag"> ({t('settings.support.optional')})</span>
            </label>
            
            {!imagePreview ? (
              <div className="image-upload-box">
                <input
                  type="file"
                  id="support-image"
                  accept="image/jpeg, image/png, image/gif, image/jpg"
                  onChange={handleImageChange}
                  className="file-input"
                />
                <label htmlFor="support-image" className="file-label">
                  <FiUpload className="upload-icon" />
                  <span>{t('settings.support.uploadImage')}</span>
                  <p className="upload-hint">{t('settings.support.uploadHint')}</p>
                </label>
              </div>
            ) : (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button 
                  type="button" 
                  className="remove-image-btn"
                  onClick={handleRemoveImage}
                >
                  <FiX /> {t('settings.support.remove')}
                </button>
              </div>
            )}
          </div>
          
          <div className="support-submit">
            <button
              type="submit"
              className="settings-button primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="button-spinner"></div> {t('settings.support.submitting')}
                </>
              ) : (
                <>
                  <FiSend className="button-icon" /> {t('settings.support.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupportForm;